// Copyright 2026 Epic Digital Interactive Media LLC
// SPDX-License-Identifier: Apache-2.0

/**
 * @fileoverview Zod schema for the git_project_links VCA collection.
 *
 * Mapping table between FlowState Projects and provider-side project/group entities.
 * Allows a single FlowState project to track work across multiple provider projects
 * (e.g. a GitHub org with several repos grouped under one FlowState project).
 *
 * Identity tuple: (flowstateProjectId, gitConnectionId, externalProjectId)
 * ID prefix: gpl_
 */

import { z } from 'zod'

export const git_project_linksSchema = z.object({
  id: z.string().max(100),
  orgId: z.string().max(100),
  workspaceId: z.string().max(100).optional(),

  // ── Identity tuple ────────────────────────────────────────────────────
  /** FlowState project ID (FK → projects.id). */
  flowstateProjectId: z.string().max(100),
  /** FK → git_connections.id */
  gitConnectionId: z.string().max(100),
  /** Provider-side project / group / namespace identifier. */
  externalProjectId: z.string().max(200),

  // ── Link metadata ─────────────────────────────────────────────────────
  /** Web URL of the external project for deep-linking in the UI. */
  externalProjectUrl: z.string().max(1000).optional(),
  /**
   * Which FlowState fields are kept in sync from the provider.
   * Example: ['title', 'description', 'members']
   */
  syncedFields: z.array(z.string()).optional(),

  // ── Provider-specific extras ──────────────────────────────────────────
  providerData: z.record(z.string(), z.unknown()).optional(),
})

export type Git_project_linksInput = z.infer<typeof git_project_linksSchema>
